import { BottomAction } from '@euler/app/components/BottomAction';
import { LinkButton } from '@euler/app/components/LinkButton';
import {
  DigitalSignCanvas,
  DigitalSignCanvasRef,
} from '@euler/app/components/signature/DigitalSignCanvas';
import { Typography } from '@euler/app/components/typography';
import { AsideContext } from '@euler/components/Aside';
import { HStack } from '@euler/components/layout/Stack';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { showAlert } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { memo, useCallback, useContext, useRef, useState } from 'react';
import { Platform, StyleSheet, View, useWindowDimensions } from 'react-native';
import Modal from 'react-native-modal';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import ViewShot from 'react-native-view-shot';

const kActionBarHeight = 56;
const kBottomActionHeight = 78;

export const DigitalSignModal = memo(
  ({
    loading,
    visible,
    onConfirm,
    onHide,
    onDismiss,
    onChange,
  }: {
    visible?: boolean;
    loading?: boolean;
    onConfirm?: (uri: string) => void;
    onChange?: (path: string) => void;
    onDismiss?: () => void;
    onHide?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const { bottom } = useSafeAreaInsets();
    const viewShotRef = useRef<ViewShot>(null);
    const canvasRef = useRef<DigitalSignCanvasRef>(null);
    const aside = useContext(AsideContext);
    const dimens = useWindowDimensions();
    const [hasContents, setHasContents] = useState(false);

    const width = aside?.width ?? dimens.width;

    const onClear = useCallback(() => {
      canvasRef.current?.clear();
    }, []);

    const handleChange = useCallback(
      (path: string) => {
        setHasContents(path.length > 0);
        onChange?.(path);
      },
      [onChange],
    );

    const handleConfirm = usePersistFn(async () => {
      const uri = await viewShotRef.current?.capture?.();
      if (uri == null) {
        void showAlert('获取签名内容失败，请稍后重试!');
        return;
      }
      onConfirm?.(uri);
    });

    return (
      <Modal
        isVisible={visible}
        backdropOpacity={0.5}
        onModalHide={onHide}
        style={{
          justifyContent: 'flex-end',
          alignItems: aside ? 'flex-end' : 'center',
          margin: 0,
        }}
      >
        <View
          css={`
            background-color: #fff;
            justify-content: center;
            align-items: stretch;
            width: ${width}px;
            height: ${width * 0.8 +
            rpx(kActionBarHeight) +
            rpx(kBottomActionHeight)}px;
            padding-bottom: ${bottom + rpx(kBottomActionHeight)}px;
          `}
        >
          <View
            css={`
              height: ${rpx(kActionBarHeight)}px;
              justify-content: center;
            `}
          >
            <Typography.Label align="center" medium size={rpx(16)}>
              客户签字
            </Typography.Label>
            <HStack
              style={StyleSheet.absoluteFill}
              css={`
                padding: 0 ${rpx(15)}px;
              `}
            >
              <LinkButton text="取消" onPress={onDismiss} disabled={loading} />
              <HStack justifyContent="flex-end">
                {hasContents && (
                  <LinkButton
                    text="清除"
                    onPress={onClear}
                    disabled={loading}
                  />
                )}
              </HStack>
            </HStack>
          </View>
          <View
            css={`
              flex: 1;
              border-width: ${rpx(2)}px;
              border-color: #ddd;
              border-style: dashed;
              border-radius: ${rpx(8)}px;
              margin: 0 ${rpx(15)}px;
            `}
          >
            <ViewShot
              ref={viewShotRef}
              options={{ format: 'jpg', quality: 0.9 }}
              css={`
                flex: 1;
              `}
            >
              <DigitalSignCanvas
                ref={canvasRef}
                onChange={handleChange}
                style={
                  Platform.OS === 'web'
                    ? { width: '100%', height: '100%' }
                    : undefined
                }
              />
            </ViewShot>
          </View>
          <BottomAction>
            <BottomAction.Button
              text="确认签字"
              variant={hasContents ? 'primary' : 'default'}
              disabled={!hasContents || loading}
              loading={loading}
              onPress={handleConfirm}
            />
          </BottomAction>
        </View>
      </Modal>
    );
  },
);
